import React, { useState, useEffect } from 'react';
import { 
    WandSparkles, Image as ImageIcon, Copy, RefreshCw, 
    Share2, AlertCircle, CheckCircle, FileText, Save, Loader2,
    Send
} from 'lucide-react';

// Shared UI
import { 
    AppCard, AppButton, SectionTitle, BlackboxLoaderIcon 
} from '../../components/SharedUI';

// Services
import { 
    generateAdConceptsLLM, 
    callImagenApi 
} from '../../services/aiService';

// Firebase Imports - Added uploadBytes for robust binary uploads
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';

// --- PREMIUM OVERLAY COMPONENT ---
const PremiumAdGenOverlay = ({ isVisible, currentStepIndex, totalVisuals = 3 }) => {
    if (!isVisible) return null;

    const baseSteps = [
        "Analyzing Audience Psychographics...",
        "Drafting High-Conversion Copy...",
        "Synthesizing Visual Art Direction..."
    ];
    
    const visualSteps = Array.from({ length: totalVisuals }, (_, i) => `Rendering Creative Variation ${i + 1}...`);
    const finalSteps = ["Finalizing Campaign Assets..."];

    const steps = [...baseSteps, ...visualSteps, ...finalSteps];
    const progress = Math.min(((currentStepIndex + 1) / steps.length) * 100, 100);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md animate-in fade-in duration-300">
            <div className="w-full max-w-2xl p-8 relative">
                <div className="flex justify-center mb-8">
                    <div className="relative">
                        <div className="absolute inset-0 bg-purple-500 blur-xl opacity-20 animate-pulse rounded-full"></div>
                        <WandSparkles className="w-24 h-24 text-white relative z-10 animate-pulse" />
                    </div>
                </div>

                <div className="w-full h-1 bg-zinc-800 rounded-full mb-8 overflow-hidden">
                    <div 
                        className="h-full bg-gradient-to-r from-purple-600 to-pink-500 transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                <div className="space-y-3 font-mono text-sm max-h-60 overflow-y-auto">
                    {steps.map((step, index) => {
                        let statusIcon;
                        let statusColor;
                        
                        if (index < currentStepIndex) {
                            statusIcon = <CheckCircle size={16} />;
                            statusColor = "text-green-500 opacity-50";
                        } else if (index === currentStepIndex) {
                            statusIcon = <Loader2 size={16} className="animate-spin" />;
                            statusColor = "text-white font-bold";
                        } else {
                            statusIcon = <div className="w-4 h-4 rounded-full border border-zinc-700" />;
                            statusColor = "text-zinc-600";
                        }

                        return (
                            <div key={index} className={`flex items-center gap-4 transition-all duration-300 ${statusColor}`}>
                                <div className="w-6 flex justify-center shrink-0">{statusIcon}</div>
                                <span>{step}</span>
                            </div>
                        );
                    })}
                </div>
                <div className="mt-8 text-center text-xs text-zinc-500 font-poppins uppercase tracking-widest">
                    MI Creative Studio Active
                </div>
            </div>
        </div>
    );
};

/**
 * Fetches an image and compresses it using a canvas.
 * This ensures CORS compliance and keeps the PDF size manageable.
 */
const getCompressedBase64 = async (url, maxWidth = 800) => {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    const bitmap = await createImageBitmap(blob);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Calculate aspect ratio
    const scaleFactor = maxWidth / bitmap.width;
    canvas.width = maxWidth;
    canvas.height = bitmap.height * scaleFactor;

    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    
    // 0.8 quality provides a 70% reduction in size with no visible loss in digital PDFs
    return canvas.toDataURL('image/jpeg', 0.8);
};

const AdCard = ({ concept, index, onRegenerateImage, onSave, onPushToBin }) => {
    const [viewMode, setViewMode] = useState('creative'); // 'creative' or 'mockup'
    const [imgLoading, setImgLoading] = useState(false);
    const [pushing, setPushing] = useState(false);

    const handlePush = async () => {
        setPushing(true);
        try {
            await onPushToBin(concept);
        } finally {
            setPushing(false);
        }
    };

    const handleRetryImage = async () => {
        setImgLoading(true);
        await onRegenerateImage(index, concept.image_prompt);
        setImgLoading(false);
    };

    return (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col h-full transition-all hover:shadow-2xl hover:shadow-purple-500/10">
            {/* --- TOP BAR: VIEW TOGGLE & ALGORITHMIC DATA --- */}
            <div className="flex bg-zinc-950 p-2 border-b border-zinc-800 justify-between items-center gap-2">
                <div className="flex gap-1 shrink-0">
                    <button 
                        onClick={() => setViewMode('creative')}
                        className={`px-3 py-1 text-[10px] font-bold rounded uppercase tracking-widest transition-all ${viewMode === 'creative' ? 'bg-purple-600 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                    >
                        Design
                    </button>
                    <button 
                        onClick={() => setViewMode('mockup')}
                        className={`px-3 py-1 text-[10px] font-bold rounded uppercase tracking-widest transition-all ${viewMode === 'mockup' ? 'bg-purple-600 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                    >
                        Mockup
                    </button>
                </div>
                
                {/* NEW: Matrix & Format Display */}
                <div className="flex gap-2 items-center overflow-hidden">
                     <span className="text-[9px] font-bold uppercase tracking-widest text-purple-400 border border-purple-500/30 px-2 py-0.5 rounded-full bg-purple-500/10 truncate" title={concept.aligned_hook}>
                         {concept.aligned_hook || "Creative Hook"}
                     </span>
                     <div className="text-[10px] font-mono text-zinc-500 bg-zinc-900 px-2 py-0.5 rounded shrink-0 border border-zinc-800">
                         {concept.format || "Static"}
                     </div>
                </div>
            </div>

            {/* --- VISUAL CANVAS --- */}
            <div className="relative aspect-square bg-black overflow-hidden flex items-center justify-center">
                {viewMode === 'creative' ? (
                    // Standard Design View
                    concept.imageUrl ? (
                        <img src={concept.imageUrl} alt="Ad Visual" className="w-full h-full object-cover animate-fade-in" />
                    ) : (
                        <div className="flex flex-col items-center opacity-40">
                            <BlackboxLoaderIcon className="w-10 h-10 mb-2" />
                            <span className="text-xs font-mono">RENDERING...</span>
                        </div>
                    )
                ) : (
                    // LIVE MOCKUP ENGINE (Simulating Platform UI)
                    <div className="w-full h-full bg-zinc-900 p-4 flex flex-col justify-center animate-in zoom-in-95 duration-300">
                        {concept.format === 'Vertical Video' ? (
                            <div className="border-4 border-zinc-800 rounded-[2rem] p-2 bg-black h-[90%] aspect-[9/16] mx-auto overflow-hidden relative shadow-2xl">
                                <img src={concept.imageUrl} className="h-full w-full object-cover opacity-80" alt="mockup" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-90" />
                                <div className="absolute bottom-10 left-4 right-4 text-white">
                                    <p className="font-bold text-sm mb-1 line-clamp-2">{concept.headline}</p>
                                    <p className="text-[10px] opacity-80 line-clamp-2">{concept.body}</p>
                                    <button className="mt-3 w-full py-2 bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold rounded">Watch Now</button>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-black border border-white/10 rounded-lg overflow-hidden shadow-2xl max-w-[280px] mx-auto">
                                <div className="p-2 flex items-center gap-2 border-b border-white/5">
                                    <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-blue-400 to-emerald-400" />
                                    <span className="text-[10px] font-bold text-white">Sponsored</span>
                                </div>
                                <img src={concept.imageUrl} className="w-full aspect-square object-cover" alt="mockup" />
                                <div className="p-3">
                                    <div className="flex gap-3 mb-2 text-white"><span className="text-xs">♥</span><span className="text-xs">🗨</span><span className="text-xs">↗</span></div>
                                    <p className="text-[10px] text-white font-bold leading-tight mb-1">{concept.headline}</p>
                                    <p className="text-[9px] text-zinc-400 line-clamp-2">{concept.body}</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* --- STRATEGY CONTENT --- */}
            <div className="p-5 flex flex-col flex-grow">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <h4 className="text-white font-bold text-lg leading-tight">{concept.headline}</h4>
                    <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-bold border ${concept.imageError ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>
                        {concept.imageError ? "SYNC ERROR" : "ACTIVE"}
                    </span>
                </div>
                
                <p className="text-zinc-400 text-sm mb-6 line-clamp-4 italic">"{concept.body}"</p>
                
                <div className="mt-auto space-y-4">
                    <div className="bg-zinc-800/50 p-3 rounded-lg border border-zinc-700/50">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                            <p className="text-[10px] font-bold text-zinc-300 uppercase tracking-widest">Target Logic</p>
                        </div>
                        <p className="text-[11px] text-zinc-500 leading-relaxed">{concept.strategy_blurb}</p>
                    </div>

                    <div className="flex gap-2">
                        {concept.imageError && (
                            <AppButton onClick={handleRetryImage} disabled={imgLoading} variant="secondary" className="bg-zinc-800" size="sm">
                                {imgLoading ? '...' : 'Retry Visual'}
                            </AppButton>
                        )}
                        <AppButton onClick={() => onSave(concept)} variant="primary" className="flex-1 bg-purple-600 hover:bg-purple-500" size="sm" icon={Save}>
                            Save Concept
                        </AppButton>
                        <AppButton
                            onClick={handlePush}
                            disabled={pushing || !concept.imageUrl}
                            variant="secondary"
                            className="bg-zinc-800"
                            size="sm"
                            icon={Send}
                            title="Push to blackboxad's Campaign Engine Asset Bin"
                        >
                            {pushing ? 'Pushing...' : 'Push to Asset Bin'}
                        </AppButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ContentCreationTab = ({ 
    filmData, 
    setNotification, 
    openCampaignModal, 
    saveFilmData, 
    credits, 
    deductCredit,
    storage 
}) => {
    const [concepts, setConcepts] = useState(filmData.ad_concepts || []);
    const [loading, setLoading] = useState(false);
    const [loadingStepIndex, setLoadingStepIndex] = useState(0);

    // --- PUSH TO BLACKBOXAD CAMPAIGN ENGINE ---
    // Mirrors BTSGallery.jsx's handleSendToCampaign — same endpoint, same
    // payload shape, so blackboxad only has to understand one contract.
    // project_id/project_name are blackboxmi's own — blackboxad auto-links
    // or creates the matching project on its side on first push.
    const handlePushToBin = async (concept) => {
        if (!concept.imageUrl) return;

        const confirmed = window.confirm(
            'Send this ad to the Campaign Engine? It will land unassigned in the Smart Asset Pool for scheduling.'
        );
        if (!confirmed) return;

        try {
            const res = await fetch(`${process.env.REACT_APP_BLACKBOXAD_URL}/api/bts-assets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    project_id: filmData.id,
                    project_name: filmData.title,
                    assets: [{
                        url: concept.imageUrl,
                        kind: 'image',
                        caption_note: [concept.headline, concept.body].filter(Boolean).join('\n\n'),
                        hook_text: concept.headline,
                        body_copy: concept.body,
                        content_format: concept.format || concept.platform,
                    }],
                    asset_urls: [concept.imageUrl], // required by the endpoint's validation, kept in sync with assets[]
                    hero_urls: [],
                    source: 'ad_studio',
                }),
            });

            if (!res.ok) throw new Error(`Campaign Engine responded ${res.status}`);

            setNotification({ type: 'success', message: 'Ad sent to Campaign Engine.' });
        } catch (error) {
            console.error('Push to Campaign Engine failed:', error);
            setNotification({ type: 'error', message: 'Failed to send ad to Campaign Engine. Check the connection and try again.' });
        }
    };

    // --- HELPER: Bulletproof Binary Upload ---
    // Converts Base64 -> Blob -> Uploads via uploadBytes (Standard Firebase SDK)
    const robustImageUpload = async (base64String, path, fileName) => {
        try {
            // 1. Efficiently convert Base64 to Blob using native fetch
            const response = await fetch(base64String);
            const blob = await response.blob();
            
            // 2. Define Reference
            const fullPath = `${path}/${fileName}`;
            const storageRef = ref(storage, fullPath);

            // 3. Upload Blob (Much more stable than uploadString)
            await uploadBytes(storageRef, blob);

            // 4. Get URL
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error("Robust Upload Failed:", error);
            throw error;
        }
    };

    // --- HELPER: Strict Sanitizer ---
    // Ensures we NEVER save a base64 string to Firestore, even if upload fails
    const sanitizeForFirestore = (conceptsList) => {
        return conceptsList.map(c => {
            const safeConcept = { ...c };
            // If it's still base64, wipe it. It's too big for DB.
            if (safeConcept.imageUrl && safeConcept.imageUrl.startsWith('data:')) {
                console.warn("Base64 detected in save payload. Removing to prevent Firestore crash.");
                safeConcept.imageUrl = null; 
                safeConcept.imageError = true;
            }
            return safeConcept;
        });
    };

const handleExportPDF = async () => {
    try {
        if (!window.jspdf || concepts.length === 0) {
            setNotification({ type: 'error', message: 'No ad concepts to export.' });
            return;
        }

        setNotification({ type: 'info', message: 'Finalizing Strategy Summary and Exporting...' });
        
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF('p', 'pt', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 50;

        const COLORS = {
            PURPLE: [147, 51, 234],
            DARK: [24, 24, 27],
            GRAY: [113, 113, 122],
            LIGHT_GRAY: [228, 228, 231],
            BG_SHADE: [244, 244, 245]
        };

        // --- 1. COVER PAGE (Minimalist Professional) ---
        doc.setFillColor(...COLORS.DARK);
        doc.rect(0, 0, pageWidth, 240, 'F'); 
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(32);
        doc.setFont("helvetica", "bold");
        doc.text("AD CAMPAIGN PITCH DECK", pageWidth / 2, 120, { align: 'center' });
        
        doc.setFontSize(14);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(180, 180, 180);
        doc.text("CREATIVE STRATEGY & VISUAL CONCEPTS", pageWidth / 2, 150, { align: 'center' });

        doc.setTextColor(...COLORS.DARK);
        doc.setFontSize(48);
        doc.setFont("helvetica", "bold");
        const projectTitle = filmData.title?.toUpperCase() || "UNTITLED PROJECT";
        doc.text(projectTitle, pageWidth / 2, pageHeight / 2 + 40, { align: 'center' });
        
        doc.setDrawColor(...COLORS.PURPLE);
        doc.setLineWidth(2);
        doc.line(pageWidth / 2 - 40, pageHeight / 2 + 65, pageWidth / 2 + 40, pageHeight / 2 + 65);

        // --- 2. INDIVIDUAL CONCEPT PAGES ---
        for (let i = 0; i < concepts.length; i++) {
            const concept = concepts[i];
            doc.addPage();
            let currentY = margin;

            doc.setFillColor(...COLORS.PURPLE);
            doc.rect(margin, currentY, 4, 20, 'F');
            doc.setFontSize(18);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...COLORS.DARK);
            doc.text(`CONCEPT ${i + 1}: ${(concept.platform || concept.format || 'GENERAL').toUpperCase()}`, margin + 15, currentY + 16);
            
            currentY += 60; 

            const imgSize = 300; 
            if (concept.imageUrl) {
                try {
                    const compressedData = await getCompressedBase64(concept.imageUrl);
                    doc.addImage(compressedData, 'JPEG', margin, currentY, imgSize, imgSize, undefined, 'FAST');
                } catch (e) {
                    doc.setDrawColor(...COLORS.GRAY);
                    doc.rect(margin, currentY, imgSize, imgSize);
                    doc.setFontSize(10);
                    doc.text("[Image Asset Sync Error]", margin + 10, currentY + 20);
                }
            }

            const contentX = margin + imgSize + 30;
            const contentWidth = pageWidth - contentX - margin;
            let textY = currentY + 20;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(22);
            doc.setTextColor(...COLORS.PURPLE);
            const headlineLines = doc.splitTextToSize(concept.headline, contentWidth);
            doc.text(headlineLines, contentX, textY);
            
            textY += (headlineLines.length * 28) + 15;

            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.DARK);
            const bodyLines = doc.splitTextToSize(concept.body, contentWidth);
            doc.text(bodyLines, contentX, textY, { lineHeightFactor: 1.4 });

            textY += (bodyLines.length * 16) + 40;

            doc.setFillColor(...COLORS.BG_SHADE);
            const rationaleLines = doc.splitTextToSize(concept.strategy_blurb, contentWidth - 30);
            const boxPadding = 40;
            const boxHeight = (rationaleLines.length * 14) + boxPadding;
            doc.roundedRect(contentX, textY, contentWidth, boxHeight, 8, 8, 'F');
            
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(...COLORS.PURPLE);
            doc.text("PSYCHOLOGICAL RATIONALE:", contentX + 15, textY + 25);
            
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...COLORS.DARK);
            doc.text(rationaleLines, contentX + 15, textY + 42, { lineHeightFactor: 1.3 });

            const promptY = pageHeight - margin - 80;
            doc.setDrawColor(...COLORS.LIGHT_GRAY);
            doc.setLineWidth(0.5);
            doc.line(margin, promptY - 20, pageWidth - margin, promptY - 20);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor(...COLORS.GRAY);
            doc.text("AI ART DIRECTION PROMPT:", margin, promptY);
            
            doc.setFont("helvetica", "italic");
            doc.setFontSize(9);
            doc.setTextColor(...COLORS.DARK);
            const promptLines = doc.splitTextToSize(concept.image_prompt, pageWidth - (margin * 2));
            doc.text(promptLines, margin, promptY + 15, { lineHeightFactor: 1.2 });
        }

        // --- 3. MARKETING STRATEGY SUMMARY PAGE ---
        doc.addPage();
        doc.setFillColor(...COLORS.DARK);
        doc.rect(0, 0, pageWidth, 120, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont("helvetica", "bold");
        doc.text("CAMPAIGN SUMMARY", margin, 70);

        let summaryY = 160;
        concepts.forEach((c, idx) => {
            doc.setFillColor(...COLORS.BG_SHADE);
            doc.roundedRect(margin, summaryY, pageWidth - (margin * 2), 70, 4, 4, 'F');
            
            doc.setTextColor(...COLORS.PURPLE);
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text(`${(c.platform || c.format || 'GENERAL').toUpperCase()} STRATEGY`, margin + 20, summaryY + 25);
            
            doc.setTextColor(...COLORS.DARK);
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            const summaryHeadline = doc.splitTextToSize(`Headline: ${c.headline}`, pageWidth - (margin * 2) - 60);
            doc.text(summaryHeadline, margin + 20, summaryY + 45);
            
            summaryY += 90;
        });

        // Pagination
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.GRAY);
            doc.text(`BLACKBOX mi | ${filmData.title || "Project"} | Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 20, { align: 'center' });
        }

        doc.save(`${filmData.title.replace(/\s+/g, '_')}_Ad_Pitch_Deck.pdf`);
        setNotification({ type: 'success', message: 'Professional Pitch Deck Exported.' });
    } catch (error) {
        console.error("Critical Export Error:", error);
        setNotification({ type: 'error', message: 'PDF Generation failed.' });
    }
};

    const handleGenerate = async () => {
        const creditCost = 2;
        if (credits < creditCost) {
            setNotification({ type: 'error', message: `Insufficient credits. Requires ${creditCost} credits.` });
            return;
        }

        const audienceAnalysis = filmData.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis;
        if (!audienceAnalysis) {
            setNotification({ type: 'error', message: 'Please run Audience Analysis first.' });
            return;
        }

        setLoading(true);
        setLoadingStepIndex(0);
        setConcepts([]); 

        try {
            // STEP 1: Text Generation
            // Gap 3: pass video_analysis so Ad Studio can use MoneyShot, Taglines, PlatformFit as creative raw material
            const videoAnalysis = filmData.video_analysis || null;
            const textConcepts = await generateAdConceptsLLM(filmData, audienceAnalysis, videoAnalysis);
            setLoadingStepIndex(2);

            await deductCredit(creditCost);

            const initialConcepts = textConcepts.map(c => ({ ...c, imageUrl: null, imageError: false }));
            setConcepts(initialConcepts);

            // STEP 2: Image Generation & Upload
            const updatedConcepts = [...initialConcepts];
            
            for (let i = 0; i < updatedConcepts.length; i++) {
                try {
                    setLoadingStepIndex(3 + i); 
                    
                    // Generate Image (Returns Base64)
                    const base64Image = await callImagenApi(updatedConcepts[i].image_prompt);
                    
                    // Upload to Firebase using new robust method
                    if (storage && base64Image && base64Image.startsWith('data:')) {
                        try {
                            const finalUrl = await robustImageUpload(
                                base64Image,
                                `${filmData.id}/ads`,
                                `ad_${Date.now()}_${i}.png`
                            );
                            updatedConcepts[i].imageUrl = finalUrl;
                            updatedConcepts[i].imageError = false;
                        } catch (uploadErr) {
                            console.error(`Upload failed for concept ${i}.`, uploadErr);
                            // Mark as error so user can retry, do NOT keep base64
                            updatedConcepts[i].imageUrl = null;
                            updatedConcepts[i].imageError = true;
                        }
                    } else {
                        // Fallback if API returns a normal URL (unlikely but possible)
                         updatedConcepts[i].imageUrl = base64Image;
                         updatedConcepts[i].imageError = false;
                    }
                    
                    setConcepts([...updatedConcepts]);

                } catch (imgError) {
                    console.error(`Image gen failed for concept ${i}:`, imgError);
                    updatedConcepts[i].imageError = true;
                    setConcepts([...updatedConcepts]);
                }
            }

            setLoadingStepIndex(3 + updatedConcepts.length);
            await new Promise(resolve => setTimeout(resolve, 800)); 

            // STEP 3: Save (Sanitized)
            const safeConcepts = sanitizeForFirestore(updatedConcepts);
            await saveFilmData({
                ...filmData,
                ad_concepts: safeConcepts
            });
            
            const failureCount = safeConcepts.filter(c => c.imageError).length;
            if (failureCount > 0) {
                 setNotification({ type: 'warning', message: `Ad Studio complete. ${failureCount} images failed to save. Click 'Retry' on the cards.` });
            } else {
                 setNotification({ type: 'success', message: 'Ad Studio generation complete!' });
            }

        } catch (error) {
            console.error("Ad Studio Error:", error);
            setNotification({ type: 'error', message: 'Failed to generate concepts.' });
        } finally {
            setLoading(false);
            setLoadingStepIndex(0);
        }
    };

    const handleRegenerateImage = async (index, prompt) => {
        const updated = [...concepts];
        updated[index].imageUrl = null;
        updated[index].imageError = false;
        setConcepts(updated);

        try {
            const base64Image = await callImagenApi(prompt);
            let finalUrl = null;
            
            if (storage && base64Image.startsWith('data:')) {
                try {
                    finalUrl = await robustImageUpload(
                        base64Image, 
                        `${filmData.id}/ads`, 
                        `ad_retry_${Date.now()}.png`
                    );
                } catch (err) {
                     console.error("Retry Upload Failed", err);
                     throw new Error("Upload to cloud failed");
                }
            } else {
                finalUrl = base64Image;
            }
            
            updated[index].imageUrl = finalUrl;
            updated[index].imageError = false;
            setConcepts(updated);
            
            // Save update (Sanitized)
            await saveFilmData({ ...filmData, ad_concepts: sanitizeForFirestore(updated) });

        } catch (error) {
            console.error("Regeneration Error:", error);
            updated[index].imageError = true;
            updated[index].imageUrl = null;
            setConcepts([...updated]);
            setNotification({ type: 'error', message: 'Retry failed. Check connection.' });
        }
    };

   const handleSaveToCampaign = async (concept) => {
        let finalImageUrl = concept.imageUrl;

        // Double check upload if it somehow is still base64
        if (finalImageUrl && finalImageUrl.startsWith('data:')) {
            setNotification({ type: 'info', message: 'Uploading image to cloud...' });
            try {
                finalImageUrl = await robustImageUpload(
                    finalImageUrl,
                    `${filmData.id}/campaigns`,
                    `campaign_${Date.now()}.png`
                );
                
                // Update local state to prevent re-upload
                const updatedConcepts = concepts.map(c => 
                    c === concept ? { ...c, imageUrl: finalImageUrl } : c
                );
                setConcepts(updatedConcepts);
                await saveFilmData({ ...filmData, ad_concepts: sanitizeForFirestore(updatedConcepts) });
                
            } catch (error) {
                setNotification({ type: 'error', message: 'Image upload failed. Saving text only.' });
                finalImageUrl = null; 
            }
        }

        // --- THE FIX: Safely handle the shift from "Platform" to "Format" ---
        // If 'platform' is missing, fallback to 'format' and format it cleanly for the DB
        const safePlatform = (concept.platform || concept.format || 'instagram').toLowerCase().replace(/\s+/g, '_');

        // Gap 1: Bundle full Andromeda context onto the campaign so GoLiveModal
        // can generate audience-specific targeting directives and fatigue signals.
        const latestAnalysis = filmData.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis;
        const matchedHook = latestAnalysis?.creative_matrix?.find(m => m.hook_name === concept.aligned_hook);
        const andromedaContext = latestAnalysis ? {
            primary_audience_profile: latestAnalysis.target_audience_breakdown?.primary_audience_profile || '',
            psychographics: latestAnalysis.target_audience_breakdown?.psychographics || [],
            asset_allocation_rationale: latestAnalysis.asset_allocation?.rationale || '',
            aligned_hook: concept.aligned_hook || '',
            psychological_trigger: matchedHook?.psychological_trigger || '',
            visual_direction: matchedHook?.visual_direction || '',
        } : {};

        // Gap 3: Carry platform fit scores from video analysis if available
        const platformFitScores = filmData.video_analysis?.PlatformFitScores || null;

        const campaignData = {
            title: concept.headline,
            content: concept.body,
            platform: safePlatform,
            status: 'Draft',
            creative: finalImageUrl ? { url: finalImageUrl } : null,
            conceptName: concept.headline,
            hypothesis: concept.strategy_blurb,
            aligned_hook: concept.aligned_hook || '',
            andromeda_context: andromedaContext,
            platform_fit_scores: platformFitScores,
        };

        openCampaignModal(campaignData);
    };

    return (
        <div className="space-y-8">
            <PremiumAdGenOverlay 
                isVisible={loading} 
                currentStepIndex={loadingStepIndex} 
                totalVisuals={concepts.length > 0 ? concepts.length : 3} 
            />

           <AppCard className="p-6 border-b border-zinc-800">
    <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
        <div>
            <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                <WandSparkles className="text-purple-500" />
                Ad Concept Studio
            </h3>
            <p className="text-zinc-400 max-w-xl">
                Generate high-converting ad variants tailored to your specific audience segments. 
                Creates headlines, copy, and visual direction automatically.
            </p>
        </div>
        <div className="flex gap-3">
            {/* NEW: Export PDF Button */}
            {concepts.length > 0 && (
                <AppButton 
                    onClick={handleExportPDF} 
                    variant="secondary"
                    size="lg" 
                    icon={FileText}
                >
                    Export Pitch Deck
                </AppButton>
            )}
            <AppButton 
                onClick={handleGenerate} 
                disabled={loading} 
                size="lg" 
                icon={WandSparkles}
                className="shadow-lg shadow-purple-900/20"
            >
                {loading ? 'Generating...' : 'Generate New Concepts (2 Credits)'}
            </AppButton>
        </div>
    </div>
</AppCard>

            {!loading && concepts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up">
                    {concepts.map((concept, i) => (
                        <AdCard 
                            key={i} 
                            index={i}
                            concept={concept} 
                            onRegenerateImage={handleRegenerateImage}
                            onSave={handleSaveToCampaign}
                            onPushToBin={handlePushToBin}
                        />
                    ))}
                </div>
            )}

            {!loading && concepts.length === 0 && (
                <div className="text-center py-20 border-2 border-dashed border-zinc-800 rounded-xl">
                    <WandSparkles className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-zinc-500">No Concepts Generated</h3>
                    <p className="text-zinc-600 mt-2">Click the button above to start your first batch.</p>
                </div>
            )}
        </div>
    );
};

export default ContentCreationTab;